import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse, HttpUserEvent, } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthenticationModel } from './auth/models/authentication.model';

import { AuthService } from './auth/services/auth.service';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  /**
   * Constructor
   *
   * @param {AuthService} authService
   * @param router
   */
  public constructor(private authService: AuthService, private router: Router) {
  }

  /**
   * Request interceptor to add the bearer token when logged in
   *
   * @param {HttpRequest<any>} request
   * @param {HttpHandler} next
   *
   * @return {Observable<HttpEvent<any>>}
   */
  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpResponse<any> | HttpUserEvent<any> | any> {

    // Exclude any URLs that do not need the Bearer token.
    if (request.url.includes('oauth')) {
      return next.handle(request);
    }

    if (AuthService.getAccessToken()) {
      request = TokenInterceptor.addTokenToRequest(request, AuthService.getAccessToken());
    }

    return next.handle(request)
    .pipe(
      catchError((error) => {
        if (!request.url.includes('oauth')) {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            return this.handle401Error(request, next);
          }
        }

        return throwError(() => error);
      }),
    );
  }

  /**
   * Adds the access token from the AuthService to the given HttpRequest.
   *
   * @param {HttpRequest<any>} request The initial HttpRequest that was sent
   * @param token
   *
   * @return {HttpRequest<any>}
   */
  private static addTokenToRequest(request: HttpRequest<any>, token: string): HttpRequest<any> {

    // Exclude any URLs that do not need the Bearer token.
    if (request.url.includes('oauth')) {
      return request.clone();
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  /**
   * Upon 401 errors, try to refresh the token. If this fails, restart the app; user is no longer logged in.
   *
   * @param request
   * @param next
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpResponse<any> | HttpUserEvent<any> | any> {
    if (!this.isRefreshing) {
      this.refreshTokenSubject.next(null);
      this.isRefreshing = true;

      return this.authService.refreshAccessToken().pipe(
        switchMap((authResult: AuthenticationModel) => {
          this.isRefreshing = false;
          const accessToken = authResult.access_token;
          this.refreshTokenSubject.next(accessToken);
          return next.handle(
            TokenInterceptor.addTokenToRequest(request, accessToken),
          );
        }),
        catchError(() => {
          this.authService.clearAccessTokenAndRestartApp();
          return throwError(() => new Error('Token expired and could not refresh'));
        }),
      );
    } else {
      return this.refreshTokenSubject
      .pipe(
        filter(token => token !== null),
        take(1),
        switchMap(currentAccessToken => {
          return next.handle(
            TokenInterceptor.addTokenToRequest(request, currentAccessToken),
          );
        }),
      );
    }
  }

}
